import { useState } from "react";
import { Download, FileSpreadsheet } from "lucide-react";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import { apiClient } from "../../api/client";
import { Button } from "./Button";
import { Modal } from "./Modal";

export interface ExportFilterField {
  key: string;
  label: string;
  type: "select" | "date" | "text";
  options?: string[];
}

interface ExportButtonProps {
  /** Percorso relativo dell'endpoint di export, es. "/interventi/export". */
  endpoint: string;
  /** Nome del file scaricato, es. "interventi.xlsx". */
  filename: string;
  /** Campi filtro mostrati nel pannello — vuoto per un export senza filtri. */
  fields?: ExportFilterField[];
  /**
   * Filtri già applicati alla vista da cui si esporta, che l'utente non
   * ricompila nel pannello. Senza, un export lanciato da una pagina filtrata
   * restituirebbe tutto: chi ha appena ristretto la mappa a due clienti e poi
   * scarica si aspetta quei due, non l'intero parco.
   *
   * Un valore array viene serializzato ripetendo la chiave
   * (`?cliente_id=a&cliente_id=b`), la forma che le rotte si aspettano.
   */
  extraParams?: Record<string, string | string[]>;
  label?: string;
}

/**
 * Bottone "Esporta Excel" con un pannello filtri opzionale — un unico
 * componente condiviso da tutti i moduli invece di ricostruire lo stesso
 * modal/logica di download in ognuno. Il download passa da apiClient (non
 * un link diretto) apposta: serve il token di autenticazione allegato,
 * stesso motivo per cui il download del template import funziona così.
 */
export function ExportButton({
  endpoint,
  filename,
  fields = [],
  extraParams,
  label = "Esporta Excel",
}: ExportButtonProps) {
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});

  async function handleDownload() {
    setIsDownloading(true);
    try {
      const params: Record<string, string | string[]> = {};
      for (const [key, value] of Object.entries(values)) {
        if (value) params[key] = value;
      }
      // I filtri della vista si aggiungono dopo quelli del pannello, ma non
      // sovrascrivono: se una chiave compare in entrambi vince quella scelta a
      // mano nel pannello, che è l'ultima azione dell'utente.
      for (const [key, value] of Object.entries(extraParams ?? {})) {
        if (params[key] === undefined && (Array.isArray(value) ? value.length > 0 : value)) params[key] = value;
      }

      const response = await apiClient.get(endpoint, {
        params,
        responseType: "blob",
        // Ripete la chiave per gli array invece di aggiungere `[]` al nome:
        // `?cliente_id=a&cliente_id=b`. Innocuo per i chiamanti che passano
        // solo valori singoli.
        paramsSerializer: { indexes: null },
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setOpen(false);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile generare il file Excel."), "error");
    } finally {
      setIsDownloading(false);
    }
  }

  // Nessun filtro configurato: scarica direttamente al click, senza aprire un pannello vuoto.
  if (fields.length === 0) {
    return (
      <Button variant="secondary" loading={isDownloading} onClick={handleDownload}>
        <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
        {label}
      </Button>
    );
  }

  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
        {label}
      </Button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={label}
        size="sm"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button type="button" loading={isDownloading} onClick={handleDownload}>
              <Download className="h-4 w-4" aria-hidden="true" />
              Scarica
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-xs text-ink/50">
            Lascia un campo vuoto per non filtrare su quello — con tutti i campi vuoti scarichi l'elenco completo.
          </p>
          {fields.map((field) => (
            <div key={field.key} className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink">{field.label}</label>
              {field.type === "select" ? (
                <select
                  value={values[field.key] ?? ""}
                  onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                >
                  <option value="">Tutti</option>
                  {field.options?.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              ) : field.type === "date" ? (
                <input
                  type="date"
                  value={values[field.key] ?? ""}
                  onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                />
              ) : (
                <input
                  type="text"
                  value={values[field.key] ?? ""}
                  onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                />
              )}
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
}
